import React, { useEffect, useRef, useState } from "react";
import AppearanceButton from "../Appearance/AppearanceButton";

/**
 * Platform-neutral Share control.
 * Prefer Web Share API; fallback to copy link + common networks.
 */
function ShareMenu({
  url,
  title = "",
  text = "",
  className = "org-action-btn",
  label = "Share",
  variant = "share",
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef(null);
  const shareUrl =
    url || (typeof window !== "undefined" ? window.location.href : "");

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handlePrimary = async () => {
    if (canNativeShare) {
      try {
        await navigator.share({
          title: title || document.title,
          text: text || title,
          url: shareUrl,
        });
        return;
      } catch (e) {
        // User cancelled or unsupported payload — open fallback menu.
      }
    }
    setOpen((v) => !v);
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const el = document.createElement("textarea");
        el.value = shareUrl;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setCopied(false);
    }
  };

  const encoded = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title || "");
  const networks = [
    {
      key: "email",
      label: "Email",
      href: `mailto:?subject=${encodedTitle}&body=${encoded}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
    },
    {
      key: "x",
      label: "X",
      href: `https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}`,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodedTitle}%20${encoded}`,
    },
    {
      key: "telegram",
      label: "Telegram",
      href: `https://t.me/share/url?url=${encoded}&text=${encodedTitle}`,
    },
  ];

  return (
    <div className="share-menu" ref={rootRef}>
      <AppearanceButton
        type="button"
        variant={variant}
        className={className}
        onClick={handlePrimary}
      >
        <i className="fas fa-share-alt org-action-icon ae-btn__icon" aria-hidden="true" />
        <span>{label}</span>
      </AppearanceButton>
      {open ? (
        <div className="share-menu__panel" role="menu" aria-label="Share options">
          <button
            type="button"
            className="share-menu__item"
            role="menuitem"
            onClick={copyLink}
          >
            {copied ? "Link copied" : "Copy link"}
          </button>
          {networks.map((n) => (
            <a
              key={n.key}
              className="share-menu__item"
              role="menuitem"
              href={n.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
            >
              {n.label}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default ShareMenu;
